import { AnimatedHeight } from '@codezo/offer-extras';
import { Card, MicroLabel, cn } from '@codezo/quote-detail';
import { useState, type ReactNode } from 'react';
import type { FlowRow, FlowRowState, FlowSection } from '../types';
import { ChevronDown } from 'lucide-react';
import { StepDot } from '../ui/StepDot';

/**
 * The order flow, as one continuous spine.
 *
 * ── Why one card and not a card per section ──
 *
 * The order is one process. Products run their own tracks inside it and can
 * diverge — one in production while another reruns sampling — but they are still
 * the same order moving, and a stack of separate cards says the opposite: that
 * these are unrelated things which happen to be listed together. One spine, with
 * section heads dividing it, keeps the divergence visible without breaking the
 * thing in half.
 *
 * ── The row grammar ──
 *
 * Every row is the same three parts: a dot on the rail, a label, and a right
 * slot. What changes is what the row expands into while it is active — sample
 * rounds, a payment panel, that item's shipments — and that content is the
 * caller's, because none of it belongs to a package that neither fetches nor
 * mutates.
 */
export function OrderFlow({
	sections,
	title = 'Order flow',
	meta,
	badge,
}: {
	sections: FlowSection[];
	title?: string;
	/** The line beside the heading — what the spine is showing. */
	meta?: ReactNode;
	/** Top-right of the card — a terminal state for the whole order. */
	badge?: ReactNode;
}) {
	if (sections.length === 0) return null;

	return (
		<Card>
			<div className="flex flex-wrap items-center gap-2.5 border-b border-line-1 px-5 py-3">
				<MicroLabel>{title}</MicroLabel>
				{meta && <span className="min-w-0 text-xs text-text-4">{meta}</span>}
				{badge && <span className="ml-auto">{badge}</span>}
			</div>

			{sections.map((section, i) => (
				<div key={section.key}>
					{/* A section may carry no rows — the client's setup, where the
					    studio's own steps are none of their business and the head and
					    its chip are the whole message. Rendering the body anyway left
					    the head's margin and an empty padded box below it. */}
					<FlowSectionHead
						section={section}
						first={i === 0}
						flush={section.rows.length === 0}
					/>
					{section.rows.length > 0 && (
						<div className="px-5 pb-1">
							{section.rows.map((row, j) => (
								<FlowRowView
									key={row.key}
									row={row}
									last={j === section.rows.length - 1}
								/>
							))}
						</div>
					)}
				</div>
			))}
		</Card>
	);
}

function FlowSectionHead({
	section,
	first,
	flush,
}: {
	section: FlowSection;
	first: boolean;
	/** No rows follow — drop the gap that would sit under it. */
	flush?: boolean;
}) {
	return (
		<div
			className={cn(
				'flex flex-wrap items-center gap-3 border-b border-line-1 px-5 py-3.5',
				!flush && 'mb-3',
				!first && 'border-t'
			)}
		>
			{section.thumb}
			<span className="min-w-0 flex-1">
				<span className="block text-base font-medium text-text-1">{section.title}</span>
				{section.meta && (
					<span className="mt-px block text-2xs text-text-4">{section.meta}</span>
				)}
			</span>
			{section.badge}
		</div>
	);
}

function FlowRowView({ row, last }: { row: FlowRow; last: boolean }) {
	const muted = row.state === 'upcoming' || row.state === 'skipped';

	return (
		<div className="flex gap-3.5">
			<FlowRail state={row.state} last={last} />
			{/* -mt-px: the design nudges the CONTENT up a pixel to sit on the dot's
			    centre line, rather than pushing the dot down. Same optical result,
			    but it keeps the rail's own geometry exact — see `FlowRail`. */}
			<div className={cn('-mt-px min-w-0 flex-1', last ? 'pb-2' : 'pb-3.5')}>
				{/* leading-[15px], not the inherited 1.5. The design's row is a
				    15px line box over a 14px gap; our body line-height made every
				    row 3px taller, which on a five-row product is 15px of drift and
				    reads as a looser, airier spine than the design's. */}
				<div className="flex min-h-[15px] flex-wrap items-center gap-2.5">
					<span
						className={cn(
							'text-sm leading-[15px]',
							row.state === 'active' ? 'font-medium text-text-1' : null,
							// An overtaken row is a row the flow REACHED. Left to
							// inherit, it read as quietly as an upcoming one, which is
							// how an unpaid sample became invisible the moment the item
							// moved on to production.
							row.state === 'done' || row.state === 'outstanding'
								? 'text-text-1'
								: null,
							muted ? 'text-text-4' : null
						)}
					>
						{row.label}
					</span>
					{(row.meta || row.right) && (
						<span className="ml-auto inline-flex items-center gap-2">
							{/* The quiet styling belongs to META, not to the cluster.
							    It used to sit on the wrapper, so anything in `right`
							    inherited `text-2xs text-text-4` — which on an outline
							    button, whose variant sets no colour of its own, reads as
							    disabled. `right` is a slot for chips and controls that
							    bring their own weight; only the meta text is quiet. */}
							{/* The TONE is the row's, the words are the caller's. An
							    outstanding row's meta is the server's sentence about what
							    is still owed, and set in the same grey as "approved Sep
							    21" it reads as another quiet date. */}
							{row.meta && (
								<span
									className={cn(
										'text-2xs',
										row.state === 'outstanding'
											? 'text-status-warn'
											: 'text-text-4'
									)}
								>
									{row.meta}
								</span>
							)}
							{row.right}
						</span>
					)}
				</div>
				{row.panel}
			</div>
		</div>
	);
}

/**
 * The dot, and the line down to the next one.
 *
 * The connector carries the accent only where the flow has actually been
 * through: past a skipped step it stays neutral, because nothing travelled that
 * way.
 */
function FlowRail({ state, last }: { state: FlowRowState; last: boolean }) {
	// Only the connector needs a state here; the mark itself is `StepDot`'s.
	const done = state === 'done';

	return (
		// 14px, not 15. The container has to be EVEN or the 4px dot centres on a
		// half pixel and sits visibly off inside its own ring — see `StepDot`. The
		// row pitch this sets is 1px tighter as a result, which moves it toward the
		// design's 31px rather than away from it.
		<div className="flex w-[14px] shrink-0 flex-col items-center">
			<StepDot size={14} state={state} />
			{/* 3px above and below, not 4. The rail sets the row's height whenever
			    the label is shorter than it is, so this margin IS the row pitch:
			    ours came out 34px against the design's 31, and five rows a product
			    of that is what made the spine read loose. */}
			{!last && (
				<span
					className="my-[3px] w-px flex-1"
					style={{
						minHeight: 10,
						background: done ? 'var(--accent)' : 'var(--line-2)',
						opacity: done ? 0.4 : 1,
					}}
				/>
			)}
		</div>
	);
}

/**
 * The inset panel a row expands into.
 *
 * Exported because the caller builds the contents — a payment choice, a set of
 * sample rounds, a list of shipments — and they should all sit in the same box
 * rather than each inventing its own.
 */
export function FlowPanel({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<div
			className={cn(
				// bg-surface, not sunken — the design's FCPanel is `var(--bg-2)`,
				// the card's own paper, held apart by its border. See `ActionBox`.
				'mt-2.5 rounded-[var(--r-2)] border border-line-2 bg-surface px-3.5 py-3',
				className
			)}
		>
			{children}
		</div>
	);
}

/**
 * A finished row's content, folded away behind a link.
 *
 * ── Why a done row needs one at all ──
 *
 * The spine opens only the ACTIVE row, which is right for anything a step can
 * still be moved with. It is wrong for what a step LEFT BEHIND: the moment a
 * client approved a sample, the round's photos, its virtual delivery, the
 * studio's note, the tracking and every earlier round went unreachable on both
 * surfaces — the row turned `done` and its panel was dropped with it. Sampling
 * is the part of an order people go back to most: it is the evidence of what was
 * agreed before anything was made.
 *
 * So the record stays reachable while the spine stays shut at rest. Closed by
 * default, because a delivered five-product order would otherwise open five full
 * sample steppers nobody asked for.
 */
export function FlowDisclosure({ label, children }: { label: string; children: ReactNode }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="mt-2">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				className="flex w-fit items-center gap-1 bg-transparent p-0 text-2xs text-text-4 transition-colors hover:text-text-1"
			>
				<ChevronDown
					size={11}
					className={cn('transition-transform duration-150', open && 'rotate-180')}
				/>
				{label}
			</button>
			{/* Measured rather than switched, so the rows below it slide instead of
			    jumping — the same treatment the product card's specifications get. */}
			<AnimatedHeight step={open ? 'open' : 'closed'}>
				{open ? children : null}
			</AnimatedHeight>
		</div>
	);
}
